import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface AdvertisementProps {
  location: 'masthead' | 'sidebar-left' | 'sidebar-right';
  className?: string;
}

interface Ad {
  id: string;
  name: string;
  type: 'image' | 'code';
  location: 'masthead' | 'sidebar-left' | 'sidebar-right';
  pages: string[];
  imageUrl?: string;
  linkUrl?: string;
  htmlCode?: string;
  isActive: boolean;
  startDate: string;
  endDate: string;
  dimensions: string;
}

// This would normally come from an API or context
const getAdsForLocation = (location: string, currentPage: string): Ad[] => {
  // Mock data - in a real app, this would be fetched from an API
  const allAds: Ad[] = [
    {
      id: '1',
      name: 'Kredi Kampanyası Banner',
      type: 'image',
      location: 'masthead',
      pages: ['ihtiyac-kredisi', 'konut-kredisi', 'tasit-kredisi'],
      imageUrl: 'https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=1200&h=200',
      linkUrl: 'https://example.com/kampanya',
      isActive: true,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      dimensions: '1200x200'
    },
    {
      id: '2',
      name: 'Mevduat Yan Banner',
      type: 'image',
      location: 'sidebar-right',
      pages: ['mevduat'],
      imageUrl: 'https://images.unsplash.com/photo-1565514020179-026b92b2d70b?auto=format&fit=crop&q=80&w=300&h=600',
      linkUrl: 'https://example.com/mevduat',
      isActive: true,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      dimensions: '300x600'
    },
    {
      id: '3',
      name: 'Kredi Kartı Sol Banner',
      type: 'image',
      location: 'sidebar-left',
      pages: ['kredi-kartlari'],
      imageUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&q=80&w=300&h=600',
      linkUrl: 'https://example.com/kredi-karti',
      isActive: true,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      dimensions: '300x600'
    },
    {
      id: '5',
      name: 'Kredi Kartı JS Kodu',
      type: 'code',
      location: 'sidebar-left',
      pages: ['kredi-kartlari'],
      htmlCode: '<div style="width:300px;height:600px;background:#f0f0f0;display:flex;align-items:center;justify-content:center;border:1px solid #ddd;"><p style="text-align:center;padding:20px;">Kredi Kartı Reklam Alanı<br>(300x600)</p></div>',
      isActive: true,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      dimensions: '300x600'
    }
  ];

  // Filter ads by location, active status, and current page
  return allAds.filter(ad => 
    ad.location === location && 
    ad.isActive && 
    ad.pages.includes(currentPage) &&
    new Date(ad.startDate) <= new Date() &&
    new Date(ad.endDate) >= new Date()
  );
};

const Advertisement: React.FC<AdvertisementProps> = ({ location, className = '' }) => {
  const [ad, setAd] = useState<Ad | null>(null);
  const [impressionRecorded, setImpressionRecorded] = useState(false);
  const { pathname } = useLocation();
  
  // Get current page from pathname
  const currentPage = pathname === '/' ? 'home' : pathname.substring(1);

  useEffect(() => {
    // Reset impression tracking when ad or location changes
    setImpressionRecorded(false);
    
    // Get ads for this location and page
    const availableAds = getAdsForLocation(location, currentPage);
    
    // If there are ads available, select one (randomly if multiple)
    if (availableAds.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableAds.length);
      setAd(availableAds[randomIndex]);
    } else {
      setAd(null);
    }
  }, [location, currentPage]);

  useEffect(() => {
    // Record impression when ad is viewed
    if (ad && !impressionRecorded) {
      // In a real app, this would call an API to record the impression
      console.log(`Ad impression recorded for ad ID: ${ad.id}`);
      setImpressionRecorded(true);
    }
  }, [ad, impressionRecorded]);

  const handleClick = () => {
    if (ad) {
      // In a real app, this would call an API to record the click
      console.log(`Ad click recorded for ad ID: ${ad.id}`);
    }
  };

  if (!ad) return null;

  // Set dimensions based on location
  let width = '100%';
  let height = 'auto';
  
  if (location === 'masthead') {
    width = '100%';
    height = '200px';
  } else {
    width = '300px';
    height = '600px';
  }

  return (
    <div 
      className={`advertisement ${className}`}
      style={{ width, height }}
    >
      {ad.type === 'image' ? (
        <a 
          href={ad.linkUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          onClick={handleClick}
          className="block w-full h-full"
        >
          <img 
            src={ad.imageUrl} 
            alt={ad.name} 
            className="w-full h-full object-cover"
          />
        </a>
      ) : (
        <div 
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: ad.htmlCode || '' }}
        />
      )}
    </div>
  );
};

export default Advertisement;